<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Chip from 'primevue/chip';
import ArchiveSwitch from '@/components/ArchiveSwitch';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import TransferModal from '@/components/Modal/TransferModal/TransferModal.vue';
import SeatsInUseModal from './SeatsInUseModal.vue';
import { useArchiveStore, useOrganizationStore, useStudentStore, useOverageStore } from '@/store';
import { OrganizationInterface, StudentTableInterface } from '@/types';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum/permissions';

const studentStore = useStudentStore();
const archiveStore = useArchiveStore();
const overageStore = useOverageStore();
const organization = useOrganizationStore().organization;
const students = ref<StudentTableInterface[]>([]);
const selectedStudents = ref<StudentTableInterface[]>([]);
const studentCount = computed(() => studentStore.totalStudents);
const studentModalOpen = ref<boolean>(false);
const transferModalOpen = ref<boolean>(false);
const selectedTransferOrganization = ref<OrganizationInterface>();
const archivedVisible = ref<boolean>(false);
const filterName = ref<string>('');
const showSeats = ref<boolean>(false);

onMounted(async () => {
  if (can([PERMISSIONS.ORGANIZATION_STUDENTS_INDEX])) {
    await getStudents();

    await getOverages(new Date());
  }
  selectedStudents.value = [];
});

const getOverages = async (date: Date) => {
  if (can([PERMISSIONS.OVERAGES_LIST])) {
    await overageStore.getOrganizationOverages(organization.id, date, true);
  }
};

const getStudents: (search?: string) => Promise<void> = async (search?: string) => {
  let filter = '?';
  if (search) filter += `search=${search}&`;
  if (archivedVisible.value) filter += 'archived=1&';

  const studentData = await studentStore.getStudentsForTable(filter).catch((e) => {
    students.value = [];
    throw e;
  });
  students.value = studentData;
};

const archiveStudents: (id: number, student: string) => Promise<void> = async (id: number, student: string) => {
  const archived_reason_id = 1;
  await archiveStore.archive(id, { archived_reason_id }).then(() => {
    archiveStore.doToast(`${student} has been  archived`, 'Archived');
    getStudents();
  });
};

const unArchiveStudents: (id: number, student: string) => Promise<void> = async (id: number, student: string) => {
  await archiveStore.unarchive(id).then(() => {
    archiveStore.doToast(`${student} has been  unarchived`, 'Unarchived');
    getStudents();
  });
};

const attachStudents: (ids: number[]) => Promise<void> = async (ids) => {
  await studentStore
    .attachStudents(ids)
    .then(() => {
      studentStore.doToast('Student(s) has been added', 'Added');

      studentStore.getTotalStudents();
    })
    .catch(() => {
      studentStore.doToast('Student(s) could not be added', 'Error', true);
    });

  await getStudents();
};

const detachStudents: (ids: number[], studentName: string) => Promise<void> = async (ids, studentName) => {
  await studentStore.detachStudents(ids);
  getStudents();
  studentStore.doToast(`${studentName} has been removed from organization`, 'Removed');
};

const createMenuModel = (item: StudentTableInterface) => {
  const menuItems = [
    {
      label: 'Remove Student',
      visible: can([PERMISSIONS.ORGANIZATION_DETACH_STUDENTS]),
      command: () => {
        detachStudents([item.id], item.name);
      },
    },
    {
      label: 'Archive',
      visible: can([PERMISSIONS.PEOPLE_ARCHIVE]),
      command: () => archiveStudents(item.id, item.name),
    },
    {
      label: 'Transfer Student',
      visible: can([PERMISSIONS.ORGANIZATION_TRANSFER_STUDENTS]),
      command: () => {
        selectedStudents.value = [item];
        openTransferModal();
      },
    },
  ];
  if (item.archived) {
    menuItems.push({
      label: 'Unarchive',
      visible: can([PERMISSIONS.PEOPLE_UNARCHIVE]),
      command: async () => unArchiveStudents(item.id, item.name),
    });
  }
  return menuItems;
};

const openAddStudents = async () => {
  studentModalOpen.value = true;
  studentStore.page = 0;
  await studentStore.getAddStudentsModalData();
};

const onLoadMoreStudents = async () => {
  await studentStore.getAddStudentsModalData();
};

const onSearch = () => {
  const search = filterName.value;
  if (search.length > 3) getStudents(search);

  if (search.length === 0) getStudents();
};

const onCancelAdd = () => (studentModalOpen.value = false);

const onSaveAdd = (students: { name: string; value: number }[]) => {
  let studentIds = students.map((student) => {
    return student.value;
  });
  attachStudents(studentIds);
  studentModalOpen.value = false;
};

const openTransferModal = () => (transferModalOpen.value = true);

const onCancelTransfer = () => (transferModalOpen.value = false);

const onTransferStudents = async (payload: { students: number[]; organization: OrganizationInterface }) => {
  const { students, organization } = payload;
  selectedTransferOrganization.value = organization;
  await studentStore
    .transferStudents(students, organization.id as number)
    .then(() => {
      studentStore.doToast(`${selectedTransferOrganization.value?.name} Transferred`, 'Student Transferred');
    })
    .catch((errorResponse: { message?: string; errors: Record<string, string[]> }) => {
      const errorArr = ['Student transfer failed.', errorResponse.data.message];
      const detail: string = errorArr.join('\n');
      studentStore.doToast(detail, 'Transfer unsuccessful', true);
    });
  await getStudents();
  selectedStudents.value = [];
};

const showArchived = () => {
  archivedVisible.value = true;
  studentStore.showArchived = true;
  getStudents();
};

const hideArchived = () => {
  archivedVisible.value = false;
  studentStore.showArchived = false;
  getStudents();
};

const toggleSeatsInUseModal = () => {
  showSeats.value = !showSeats.value;
};

const seatsScheduledExceeded = computed(() => overageStore.scheduled_over);
const seatsCompletedExceeded = computed(() => overageStore.completed_over);
</script>

<template>
  <div class="w-full flex">
    <SeatsInUseModal v-if="showSeats" :organization-id="organization.id" @cancel="toggleSeatsInUseModal" />
    <Card>
      <template #header>
        <h2>Active Students</h2>
        <Chip>{{ studentCount }}</Chip>
        <div v-if="can([PERMISSIONS.OVERAGES_LIST])" class="seats flex flex-wrap align-content-middle gap-3">
          <div
            v-if="overageStore.show_seats_modal"
            class="font-bold cursor-pointer align-content-middle gap-2 flex"
            @click="toggleSeatsInUseModal"
          >
            <span class="seats-button">See Seats In Use</span>
            <span v-if="seatsCompletedExceeded" class="alert-red font-bold">
              You've exceeded your seats for the month, which may incur fees
            </span>
            <span v-else-if="seatsScheduledExceeded" class="alert-orange font-bold">
              You are approaching your max seat count for the month. Exceeding this may incur a fee.
            </span>
          </div>
        </div>
        <div class="ml-auto">
          <AppButton
            v-if="can([PERMISSIONS.ORGANIZATION_ATTACH_STUDENTS])"
            type="text"
            size="small"
            text="Add Students"
            icon-left="add"
            @click="openAddStudents"
          />
        </div>
      </template>
      <template #content>
        <div class="search-input mb-3 flex">
          <IconField icon-position="left">
            <InputIcon>
              <Icon name="search" class="text-lg" />
            </InputIcon>
            <InputText
              v-model="filterName"
              class="p-inputtext-sm mr-2"
              placeholder="Search students"
              @input="onSearch"
            />
          </IconField>
          <ArchiveSwitch
            v-if="can([PERMISSIONS.ORGANIZATION_STUDENT_SHOW_ARCHIVE])"
            @enable="showArchived"
            @disable="hideArchived"
          />
          <AppButton
            v-if="can([PERMISSIONS.ORGANIZATION_TRANSFER_STUDENTS])"
            class="ml-auto"
            type="secondary"
            text="Transfer Organization"
            :disabled="!selectedStudents.length"
            @click="openTransferModal"
          />
        </div>
        <DataTable
          v-model:selection="selectedStudents"
          :value="students"
          :row-class="({ archived }) => (archived ? 'grey-row' : null)"
          :scrollable="true"
          loading-icon="logo-spinner"
          :loading="studentStore.loading"
          data-key="name"
          responsive-layout="scroll"
          scroll-height="440px"
          :class="{ 'table-loading': studentStore.loading }"
        >
          <template v-if="students.length">
            <Column
              selection-mode="multiple"
              header-style="justify-content: center; max-width: 3rem;"
              body-style="justify-content: center; max-width: 3rem;"
            />
            <Column
              v-if="studentStore.showArchived"
              field="archived"
              header-style="justify-content: center; max-width: 1rem;"
              body-style="justify-content: center; max-width: 1rem;"
            >
              <template #body="{ data }">
                <Icon
                  v-if="data.archived"
                  v-tooltip.top="{
                    value: 'Archived',
                    escape: true,
                    fitContent: true,
                  }"
                  name="info"
                  class="text-color-gray-dark text-base cursor-pointer"
                />
              </template>
            </Column>
            <Column
              body-style="max-width: 22rem"
              field="name"
              header="Student Name"
              header-style="max-width: 22rem"
              sortable
            >
              <template #body="{ data }">
                <RouterLink
                  v-if="can([PERMISSIONS.PEOPLE_SHOW])"
                  :to="`/user/${data.id}`"
                  class="font-semibold"
                  v-text="data.name"
                />
                <span v-else class="font-semibold" v-text="data.name" />
              </template>
            </Column>
            <Column field="disciplines" header="Disciplines" body-style="font-size: .8725rem;"></Column>
            <Column
              body-style="justify-content: center; font-size: .8725rem; max-width: 10rem;"
              field="grade"
              header="Grade"
              header-class="justify-content-center mw-10rem"
            />
            <Column
              body-style="justify-content: center; font-size: .8725rem; max-width: 10rem;"
              field="age"
              header="Age"
              header-class="justify-content-center mw-10rem"
            />
            <Column
              body-style="justify-content: center; font-size: .8725rem; max-width: 10rem;"
              field="notifications"
              header="Notifications"
              header-class="justify-content-center mw-10rem"
            >
              <template #body="{ data }">
                <Icon v-if="data.notifications" name="notifications_active" class="text-md text-teal-500" />
                <Icon v-else name="notifications_off" class="text-md alert-red" />
              </template>
            </Column>
            <Column
              v-if="
                can([
                  PERMISSIONS.ORGANIZATION_DETACH_STUDENTS,
                  PERMISSIONS.PEOPLE_ARCHIVE,
                  PERMISSIONS.ORGANIZATION_TRANSFER_STUDENTS,
                ])
              "
              body-style="justify-content: center; font-size: .8725rem; max-width: 10rem;"
              header-class="justify-content-center mw-10rem"
              header="Actions"
            >
              <template #body="{ data }">
                <ActionMenu :menu-items="createMenuModel(data)" icon="more_horiz" />
              </template>
            </Column>
          </template>
          <template #empty>Students not assigned</template>
        </DataTable>
        <AddPersonModal
          v-if="studentModalOpen"
          title="Add students"
          :items="studentStore.studentModalData"
          :loading="studentStore.modalDataLoading"
          :searchable="true"
          button-text="Add students"
          placeholder="Search students"
          person-type="students"
          list-header-text="Student name"
          @add-items="onSaveAdd"
          @cancel="onCancelAdd"
          @load-more-items="onLoadMoreStudents"
        />
        <TransferModal
          v-if="transferModalOpen"
          :students="selectedStudents"
          :organization="organization"
          @cancel="onCancelTransfer"
          @transfer="onTransferStudents"
        />
        <Toast class="transfer-success__toast w-20rem" position="bottom-right" group="transfer-success">
          <template #message="slotProps">
            <div class="flex flex-row">
              <Icon class="md-24 success__toast__icon" name="check_circle" />
              <div class="ml-3">
                <h3 class="font-semibold mb-1">{{ slotProps.message.summary }}</h3>
                <RouterLink
                  :to="`/organizations/${selectedTransferOrganization?.id}`"
                  class="underline"
                  v-text="slotProps.message.detail"
                />
              </div>
            </div>
          </template>
        </Toast>
      </template>
    </Card>
  </div>
</template>

<style lang="scss" scoped>
.p-card {
  min-width: calc(100% - 1.5rem);
}

.transfer-success__toast {
  .success__toast__icon {
    color: var(--primary-color-medium);
  }
}

.seats-button {
  color: var(--primary-color-dark);
}

.text-red-700 {
  color: $errorColor;
}
</style>
